import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import perplexityService from '../services/perplexityService';


import { trackApiCall } from '../services/apiMetricsService';

export function useAgentChat(userId, agentId, conversationId, streaming = true) {
  const [response, setResponse] = useState('');
  const [fullResponse, setFullResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [abortController, setAbortController] = useState(null);

  // Cancel ongoing request
  const cancelRequest = useCallback(() => {
    if (abortController) {
      abortController?.abort();
      setAbortController(null);
      setIsLoading(false);
    }
  }, [abortController]);

  const detectSearchIntent = (message) => {
    const lowerMessage = message?.toLowerCase() || '';
    
    // Perform web search for Marketing Operations and Feedback/Analytics agents
    if (agentId !== 'marketing-ops' && agentId !== 'feedback-analytics') {
      return { shouldSearch: false };
    }
    
    const searchKeywords = [
      'search', 'research', 'find', 'trending', 'latest',
      'competitor', 'high-performing', 'best ads', 'industry',
      'benchmark', 'statistics', 'what are', 'show me',
      'best practice', 'market trend', 'compare', 'analyze'
    ];
    
    const shouldSearch = searchKeywords?.some(keyword => lowerMessage?.includes(keyword));
    
    if (!shouldSearch) {
      return { shouldSearch: false };
    }
    
    // Determine search type based on agent and keywords
    let searchType = 'general';
    let searchDepth = 'medium';
    
    if (agentId === 'feedback-analytics') {
      // Analytics-specific search types
      if (lowerMessage?.includes('competitor')) {
        searchType = 'competitor';
        searchDepth = 'high';
      } else if (lowerMessage?.includes('benchmark') || lowerMessage?.includes('industry standard')) {
        searchType = 'benchmark';
        searchDepth = 'high';
      } else if (lowerMessage?.includes('trend') || lowerMessage?.includes('market')) {
        searchType = 'trends';
        searchDepth = 'high';
      } else if (lowerMessage?.includes('best practice')) {
        searchType = 'best_practices';
        searchDepth = 'medium';
      }
    } else if (agentId === 'marketing-ops') {
      // Marketing-specific search types
      if (lowerMessage?.includes('competitor')) {
        searchType = 'competitor';
        searchDepth = 'high';
      } else if (lowerMessage?.includes('ad') || lowerMessage?.includes('campaign')) {
        searchType = 'ads';
        searchDepth = 'high';
      } else if (lowerMessage?.includes('trending') || lowerMessage?.includes('latest')) {
        searchType = 'trends';
        searchDepth = 'high';
      } else if (lowerMessage?.includes('statistic') || lowerMessage?.includes('benchmark')) {
        searchType = 'stats';
        searchDepth = 'medium';
      }
    }
    
    return { shouldSearch: true, searchType, searchDepth };
  };

  const performWebSearch = async (message, searchType, searchDepth) => {
    try {
      let searchResult;
      
      switch (searchType) {
        case 'ads':
          const industryMatch = message?.match(/in (?:the )?([\w\s]+?)(?:industry|sector|market)?/i);
          const industry = industryMatch?.[1] || 'general';
          searchResult = await perplexityService?.searchHighPerformingAds(industry, { searchDepth });
          break;
          
        case 'trends':
          const platformMatch = message?.match(/for ([\w\s]+?)(?:marketing|content|platform)?/i);
          const platform = platformMatch?.[1] || 'all platforms';
          searchResult = await perplexityService?.searchTrendingStrategies(platform, { searchDepth });
          break;
          
        case 'competitor':
          const competitorMatch = message?.match(/(?:competitor|company|brand)\s+([\w\s]+)/i);
          const competitor = competitorMatch?.[1] || message;
          searchResult = await perplexityService?.analyzeCompetitorCampaigns(competitor, { searchDepth });
          break;
          
        case 'stats':
          const topicMatch = message?.match(/(?:statistics|stats|data|benchmarks?)\s+(?:for|on|about)\s+([\w\s]+)/i);
          const topic = topicMatch?.[1] || message;
          searchResult = await perplexityService?.searchMarketingStats(topic, { searchDepth });
          break;
          
        default:
          searchResult = await perplexityService?.searchWeb(message, { searchDepth });
      }
      
      return searchResult;
    } catch (error) {
      console.error('Web search error:', error);
      return null;
    }
  };

  const sendMessage = useCallback(async (message, priority = 'normal') => {
    if (!userId || !agentId || !message?.trim()) {
      setError(new Error('Missing required parameters'));
      return;
    }

    setIsLoading(true);
    setError(null);
    setResponse('');
    setSearchResults(null);

    const startTime = Date.now();
    let apiCallSuccess = false;
    let apiCallError = null;

    try {
      // Detect if web search is needed
      const searchIntent = detectSearchIntent(message);
      let searchData = null;
      
      if (searchIntent?.shouldSearch) {
        // Perform web search first
        const controller = new AbortController();
        setAbortController(controller);
        const timeout = setTimeout(() => controller?.abort(), 10000); // 10 seconds timeout
        try {
          searchData = await performWebSearch(
            message,
            searchIntent?.searchType,
            searchIntent?.searchDepth
          );
          clearTimeout(timeout);
          setAbortController(null);
          
          if (searchData) {
            setSearchResults(searchData);
          }
        } catch (searchError) {
          console.error('Search timeout or error:', searchError);
          clearTimeout(timeout);
          setAbortController(null);
        }
      }

      // Call agent chat edge function
      const { data: { session } } = await supabase?.auth?.getSession();
      
      if (!session) {
        throw new Error('No active session');
      }

      const edgeFunctionUrl = `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/agent_chat`;
      
      const controller = new AbortController();
      setAbortController(controller);
      const timeout = setTimeout(() => controller?.abort(), 60000); // 60 second timeout

      try {
        const response = await fetch(edgeFunctionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`
          },
          body: JSON.stringify({
            message,
            agentId,
            conversationId,
            priority,
            searchContext: searchData,
            streaming
          }),
          signal: controller?.signal
        });

        clearTimeout(timeout);
        setAbortController(null);

        if (!response?.ok) {
          const errorData = await response?.json();
          throw new Error(errorData?.error || 'Agent chat failed');
        }

        if (streaming) {
          const reader = response?.body?.getReader();
          const decoder = new TextDecoder();
          let accumulatedResponse = '';

          while (true) {
            const { done, value } = await reader?.read();
            if (done) break;

            const chunk = decoder?.decode(value, { stream: true });
            accumulatedResponse += chunk;
            setResponse(accumulatedResponse);
          }

          setFullResponse(accumulatedResponse);
          apiCallSuccess = true;
        } else {
          const data = await response?.json();
          setResponse(data?.response);
          setFullResponse(data);
          apiCallSuccess = true;
        }
      } catch (fetchError) {
        clearTimeout(timeout);
        setAbortController(null);
        apiCallError = fetchError;
        throw fetchError;
      }
    } catch (err) {
      console.error('Agent chat error:', err);
      setError(err);
      apiCallSuccess = false;
      apiCallError = err;
    } finally {
      setIsLoading(false);
      
      // Track API call metrics
      const responseTime = Date.now() - startTime;
      await trackApiCall({
        provider: 'openai', // Agent chat uses OpenAI
        endpoint: '/agent_chat',
        responseTimeMs: responseTime,
        retryCount: 0, // Edge function handles retries internally
        success: apiCallSuccess,
        errorType: apiCallError?.name || null,
        errorMessage: apiCallError?.message || null,
        statusCode: apiCallSuccess ? 200 : 0,
        metadata: { agentId, streaming, hasSearchContext: !!searchData }
      });
    }
  }, [userId, agentId, conversationId, streaming]);

  return {
    response,
    fullResponse,
    isLoading,
    error,
    searchResults,
    sendMessage,
    cancelRequest
  };
}

export default useAgentChat;